import * as React from 'react';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@frontend/shadcn/components/ui/dialog';
import { ArrowsRotateIcon, SackDollarIcon } from '@revfluence/fresh-icons/regular/esm';
import { BOOST_TYPE, BoostEligibilitySelectionType, BoostInput, PFA_BOOST_TYPE } from '@frontend/applications/AffiliatesApp/types/globalTypes';
import { Gift } from 'lucide-react';
import { format } from 'date-fns';
import TagsIcon from '@revfluence/fresh-icons/solid/esm/TagsIcon';
import { BOOST_CONDITIONS_OPTIONS } from './BoostDrawer';

interface IProps {
  isBoostPreviewOpen: boolean;
  setIsBoostPreviewOpen: (isModalOpen: boolean) => void;
  boost: BoostInput;
  onEdit: (e: React.MouseEvent) => void;
}

function BoostSummaryLine({ triggerType, triggerValue }: { triggerType: string; triggerValue?: string | number }) {
  const option = BOOST_CONDITIONS_OPTIONS.find((opt) => opt.value === triggerType);
  if (!option) return null;
  const isEvery = triggerType.includes('EVERY');
  // Replace X with triggerValue in the label
  let label = option.label;
  if (typeof triggerValue !== 'undefined' && label.includes('X')) {
    label = label.replace(/X/g, String(triggerValue));
  }
  return (
    <span className="flex items-center gap-1 text-grey-5 text-xs font-normal">
      {label}
      {isEvery ? (
        <ArrowsRotateIcon className="w-3 h-3 text-grey-3 ml-1" />
      ) : (
        <div className="flex items-center justify-center w-3 h-3 rounded-full bg-grey-2 text-grey-foreground text-[8px] font-semibold ml-2">
          1
        </div>
      )}
    </span>
  );
}

export const BoostPreview = ({ isBoostPreviewOpen, setIsBoostPreviewOpen, boost, onEdit }: IProps) => {
  // Early return if boost is null or undefined
  if (!boost) {
    return null;
  }

  const { boostMetadata, boostType } = boost;

  const getPFABoostDetailsList = () => {
    if (!boostMetadata) return null;

    if (boostMetadata.pfaBoostType === PFA_BOOST_TYPE.PRODUCT_CATALOG) {
      return (
        <>
          {boostMetadata?.catalogId && (<li>Members will receive a {boostMetadata.catalogName} catalog.</li>)}
          {boostMetadata?.catalogSelectionRuleId && (<li>{boostMetadata.catalogSelectionRuleName} applies as the selection rule.</li>)}
        </>
      );
    } else if (boostMetadata.pfaBoostType === PFA_BOOST_TYPE.DIRECT_PRODUCT) {
      return (
        <>
          {boostMetadata?.giftProducts?.length > 0 && (<li>Members will receive gift products selected.</li>)}
        </>
      );
    }
    return null;
  };

  const getBoostPreviewIcon = () => {
    switch (boostType) {
      case BOOST_TYPE.PFA_BOOST:
        return (
          <div className="flex items-center justify-center rounded-full bg-purple-300 w-[54px] h-[54px]">
            <Gift className="text-grey-0 w-6 h-5" />
          </div>
        );
      case BOOST_TYPE.CASHBACK_BOOST:
        return (
          <div className="flex items-center justify-center rounded-full bg-purple-300 w-[54px] h-[54px]">
            <SackDollarIcon className="text-grey-0 w-6 h-5" />
          </div>
        );
      case BOOST_TYPE.DISCOUNT_BOOST:
        return (
          <div className="flex items-center justify-center rounded-full bg-teal-4 w-[54px] h-[54px]">
            <TagsIcon className="text-grey-0 w-6 h-5" />
          </div>
        );
      default:
        return null;
    }
  };
  return (
    <Dialog open={isBoostPreviewOpen} onOpenChange={() => setIsBoostPreviewOpen(false)}>
      <DialogContent className="w-[506px] rounded-2xl bg-grey-0 p-0 gap-0">
        <DialogHeader className="flex flex-row items-center justify-between p-4 space-y-0">
          <DialogTitle>
            <p className="text-primary text-base font-medium m-0 flex items-center">Reward Preview</p>
          </DialogTitle>
        </DialogHeader>
        <div className="border-b" />
        <div className="flex flex-col gap-2 p-4">
          <div className="flex items-center bg-grey-1 rounded-2xl p-3 gap-2">
            {getBoostPreviewIcon()}
            <div className="flex flex-col gap-1 flex-1">
              <span className="text-primary text-sm font-medium">{boost.label}</span>
              <BoostSummaryLine triggerType={boost.triggerType} triggerValue={boost.triggerValue} />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-grey-5 text-sm font-medium">Reward Details</span>
            <ul className="list-disc pl-5 text-grey-5 text-sm font-normal flex flex-col gap-1">
              {boost?.boostEligibility?.eligibilityType === BoostEligibilitySelectionType.ALL && <li>Applies to all members</li>}
              {boost.requireApproval && (<li>Reward requires approval</li>)}
              {boost.hideInCreatorPortal && (<li>Hidden from members</li>)}
              {boost.maxLimit ? <li>Max limit {boost.maxLimit}</li> : <li>No max limit</li>}
              {boostType === BOOST_TYPE.PFA_BOOST && getPFABoostDetailsList()}
              {boost.activeStartDate && (
                <li>
                  Active from {format(new Date(boost.activeStartDate), 'MMM dd, yyyy')}
                  {boost.activeEndDate && (
                    <> till {format(new Date(boost.activeEndDate), 'MMM dd, yyyy')}</>
                  )}
                </li>
              )}
            </ul>
          </div>
        </div>
        <div className="border-b" />
        <DialogFooter className="p-4">
          <Button variant="primary" type="submit" onClick={onEdit}>Edit</Button>
          <Button key="cancel" variant="secondary" onClick={() => setIsBoostPreviewOpen(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
